import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Motorová pila - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: NV 339/2017 Sb. (práce v lese a na pracovištích obdobného
 * charakteru - mj. řetězové pily). THEORY ONLY - practical instruction with
 * the saw is legally required on top of this course (the seed carries the
 * CHAINSAW_PRACTICAL qualification ref → theory-only banner in the player).
 * Bilingual (cs-CZ + en-US). Three chapters (OOPP a kontrola | zpětný vrh |
 * organizace práce) + a graded quiz with one question per kind. Answer keys
 * live here (repo-privileged) and in the DB; stripped from the client payload.
 */
export const chainsawProfile: TrainingProfile = {
  slug: 'chainsaw',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Motorová pila',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-oopp',
        title: t('OOPP a kontrola pily', 'PPE and saw check'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-oopp',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Bez protipořezu neřež', 'No cut-protection, no cutting'),
          },
          {
            id: 'p-oopp-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'K pile patří **protipořezové kalhoty a obuv**, **přilba s hledím a chrániči sluchu** a pevné rukavice. Před startem zkontroluj **brzdu řetězu**, **napnutí a naostření řetězu**, zachycovač řetězu a funkční pojistku plynu. Tankuj jen s vypnutým, vychladlým motorem.',
              'Chainsaw work requires **cut-protection trousers and boots**, a **helmet with visor and ear defenders**, and sturdy gloves. Before starting, check the **chain brake**, **chain tension and sharpness**, the chain catcher and the throttle lock. Refuel only with the engine off and cooled.',
            ),
          },
          {
            id: 'c-oopp-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 339/2017 Sb.** (práce v lese a na pracovištích obdobného charakteru). Tento kurz je **teorie** - praktický zácvik s pilou je povinný zvlášť.',
              'Legal basis: **Gov. Reg. 339/2017 Coll.** (forestry-type work). This course is **theory** - hands-on practical instruction with the saw is mandatory separately.',
            ),
          },
          {
            id: 'cl-oopp',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t('Protipořezové kalhoty a obuv', 'Cut-protection trousers and boots'),
              t('Přilba s hledím + chrániče sluchu', 'Helmet with visor + ear defenders'),
              t('Funkční brzda řetězu', 'Working chain brake'),
              t('Napnutý a naostřený řetěz', 'Tensioned, sharp chain'),
            ],
          },
        ],
      },
      {
        id: 'ch-vrh',
        title: t('Zpětný vrh (kickback)', 'Kickback'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-vrh'],
        blocks: [
          {
            id: 'h-vrh',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Nejnebezpečnější čtvrtina lišty', 'The most dangerous quarter of the bar'),
          },
          {
            id: 'p-vrh-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Zpětný vrh** vymrští pilu obloukem proti hlavě, když se **horní čtvrtina špičky lišty** dotkne dřeva nebo se řetěz skřípne. Proto: nikdy neřež špičkou, drž pilu **oběma rukama s palcem obepnutým kolem madla**, stůj **stranou roviny řezu** a řež plynem naplno.',
              '**Kickback** hurls the saw in an arc toward your head when the **upper quarter of the bar tip** touches wood or the chain pinches. Therefore: never cut with the tip, hold the saw **with both hands, thumbs wrapped around the handles**, stand **beside the cutting plane**, and cut at full throttle.',
            ),
          },
          {
            id: 'anim-vrh',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'chainsawKickback',
            steps: [
              {
                label: t('Řez špičkou lišty', 'Cutting with the bar tip'),
                say: t(
                  'Zpětný vrh: lišta letí obloukem k hlavě.',
                  'Kickback: the bar arcs up towards the head.',
                ),
              },
              {
                label: t('Bezpečný řez', 'Safe cut'),
                say: t(
                  'Řež spodní částí lišty, obě ruce na rukojetích, brzda řetězu funkční.',
                  'Cut with the lower part of the bar, both hands on the grips, chain brake working.',
                ),
              },
            ],
            caption: t(
              'Řez špičkou lišty = zpětný vrh k hlavě. Správný postoj: obě ruce, tělo mimo rovinu řezu.',
              'Cutting with the bar tip = kickback toward the head. Correct stance: both hands, body out of the cutting plane.',
            ),
          },
          {
            id: 'kc-vrh',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Která část lišty vyvolá zpětný vrh?',
              'Which part of the bar causes kickback?',
            ),
            choices: [
              { id: 'kc-a', label: t('Spodní hrana uprostřed', 'The bottom edge, mid-bar') },
              {
                id: 'kc-b',
                label: t('Horní čtvrtina špičky lišty', 'The upper quarter of the bar tip'),
              },
              { id: 'kc-c', label: t('Část u krytu motoru', 'The section by the engine housing') },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Dotek horní čtvrtiny špičky s materiálem pilu vymrští nahoru a dozadu - proti obsluze.',
              "Contact of the tip's upper quarter with material throws the saw up and back - toward the operator.",
            ),
          },
        ],
      },
      {
        id: 'ch-organizace',
        title: t('Organizace práce', 'Organising the work'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-kaceni'],
        blocks: [
          {
            id: 'h-organizace',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('S pilou nikdy sám', 'Never alone with a saw'),
          },
          {
            id: 'p-organizace-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'S motorovou pilou **nepracuj osamoceně** - v dosahu musí být další osoba schopná přivolat pomoc. Při kácení si předem připrav **ústupovou cestu šikmo dozadu** a drž ostatní ve vzdálenosti **dvojnásobku výšky stromu**. Dělej přestávky - vibrace a únava zhoršují úsudek.',
              'Never work **alone** with a chainsaw - another person able to call for help must be within reach. When felling, prepare an **escape path diagonally backwards** and keep others at **twice the tree height**. Take breaks - vibration and fatigue impair judgement.',
            ),
          },
          {
            id: 'ord-kaceni',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t('Seřaď přípravu kácení stromu.', 'Order the felling preparation.'),
            items: [
              {
                id: 'ok-1',
                label: t(
                  'Posouzení stromu a okolí (náklon, suché větve)',
                  'Assess the tree and surroundings (lean, dead branches)',
                ),
              },
              { id: 'ok-2', label: t('Vyklizení ústupové cesty', 'Clear the escape path') },
              {
                id: 'ok-3',
                label: t(
                  'Vykázání osob na dvojnásobek výšky stromu',
                  'Move people to twice the tree height',
                ),
              },
              {
                id: 'ok-4',
                label: t('Směrový zářez a hlavní řez', 'Directional notch, then the felling cut'),
              },
            ],
          },
          {
            id: 'k-organizace',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**Teorie ti pilu do ruky nedává** - praktický zácvik je povinný. Základ: OOPP, žádné řezání špičkou, nikdy sám.',
              "**Theory alone doesn't put the saw in your hand** - practical instruction is mandatory. Basics: PPE, never cut with the tip, never work alone.",
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-oopp-single',
      kind: 'single',
      chapterId: 'ch-oopp',
      prompt: t(
        'Bez kterého OOPP nesmíš s pilou vůbec řezat?',
        'Without which PPE must you never cut at all?',
      ),
      choices: [
        { id: 'a', label: t('Protipořezové kalhoty', 'Cut-protection trousers') },
        { id: 'b', label: t('Reflexní vesta', 'Hi-vis vest') },
        { id: 'c', label: t('Svářečská kukla', 'Welding helmet') },
      ],
      correct: 'a',
    },
    {
      id: 'q-sam-tf',
      kind: 'truefalse',
      chapterId: 'ch-organizace',
      prompt: t(
        'Krátké řezání na zahradě zákazníka je v pořádku i úplně o samotě.',
        "A quick cut in a customer's garden is fine even completely alone.",
      ),
      correct: false,
    },
    {
      id: 'q-vrh-multi',
      kind: 'multi',
      chapterId: 'ch-vrh',
      prompt: t(
        'Co snižuje riziko zpětného vrhu? (vyber všechny správné)',
        'What reduces kickback risk? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Neřezat špičkou lišty', 'Not cutting with the bar tip') },
        { id: 'b', label: t('Obě ruce, palec kolem madla', 'Both hands, thumb around the handle') },
        {
          id: 'c',
          label: t('Řezání jednou rukou pro lepší dosah', 'One-handed cutting for better reach'),
        },
        { id: 'd', label: t('Postoj mimo rovinu řezu', 'Standing out of the cutting plane') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-vzdalenost-numeric',
      kind: 'numeric',
      chapterId: 'ch-organizace',
      prompt: t(
        'V jaké vzdálenosti (násobek výšky stromu) musí být při kácení ostatní osoby?',
        'At what distance (multiple of the tree height) must others stand during felling?',
      ),
      correct: 2,
      tolerance: 0,
    },
    {
      id: 'q-kaceni-order',
      kind: 'ordering',
      chapterId: 'ch-organizace',
      prompt: t('Seřaď přípravu kácení.', 'Order the felling preparation.'),
      choices: [
        { id: 'a', label: t('Posouzení stromu', 'Assess the tree') },
        { id: 'b', label: t('Ústupová cesta', 'Escape path') },
        { id: 'c', label: t('Vykázání osob', 'Move people away') },
        { id: 'd', label: t('Zářez a hlavní řez', 'Notch and felling cut') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
